import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Select from "react-select";
import { supabase } from "../supabaseClient";
import Swal from "sweetalert2";
import { storeChatHistory } from "../chatHistory";

const zoomeyApiUrl = process.env.REACT_APP_ZOOMEY_API_URL;

const AddToSessionModal = ({
  show,
  handleClose,
  activeSessionId,
  defaultParentId,
  addToChatText,
  userId,
}) => {
  // States for parent/child session lists and selection
  const [parentSessions, setParentSessions] = useState([]);
  const [childSessions, setChildSessions] = useState({});
  const [selectedParent, setSelectedParent] = useState(defaultParentId || "");
  const [childList, setChildList] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [mainSessions, setMainSessions] = useState([]);

  // Fetch parent sessions and all child sessions on modal open
  useEffect(() => {
    if (show) {
      const fetchSessions = async () => {
        // Fetch main sessions
        const { data: mains, error: mainError } = await supabase
          .from("chat_session")
          .select("*")
          .eq("type", "main")
          .eq("user_auth", userId)
          .order("updated_at", { ascending: false });

        // Fetch all parent sessions
        const { data: parents, error: parentError } = await supabase
          .from("chat_session")
          .select("*")
          .eq("type", "topic")
          .eq("user_auth", userId)
          .is("parent_session", null)
          .order("updated_at", { ascending: false });

        // Fetch all child sessions
        const { data: children, error: childError } = await supabase
          .from("chat_session")
          .select("*")
          .eq("type", "sub_topic")
          .eq("user_auth", userId)
          .not("parent_session", "is", null)
          .order("updated_at", { ascending: false });

        if (!mainError && mains) setMainSessions(mains);
        if (!parentError && parents) {
          setParentSessions(parents);
          let defaultId =
            defaultParentId || activeSessionId || parents[0]?.id || "";
          setSelectedParent(defaultId);
        }
        if (!childError && children) {
          const grouped = {};
          children.forEach((child) => {
            if (!grouped[child.parent_session])
              grouped[child.parent_session] = [];
            grouped[child.parent_session].push(child);
          });
          setChildSessions(grouped);
        }
      };
      fetchSessions();
    }
  }, [show, defaultParentId, activeSessionId, userId]);

  // Fetch childList for selected parent only
  useEffect(() => {
    if (selectedParent && childSessions[selectedParent]) {
      setChildList(childSessions[selectedParent]);
    } else {
      setChildList([]);
    }
  }, [selectedParent, childSessions]);

  const addToSession = async () => {
    try {
      setIsAdding(true);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      await storeChatHistory(selectedParent, addToChatText, "text", false, null, null, userId);

      const allOptions = parentSessions.flatMap((parent) => {
        const parentOption = {
          value: parent.id,
          label: parent.name,
          isChild: false,
        };
        const children = childSessions[parent.id] || [];
        const childOptions = children.map((child) => ({
          value: child.id,
          label: `${child.name} (subtopic)`,
          isChild: true,
        }));
        return [parentOption, ...childOptions];
      });

      const selectedSession = allOptions.find(
        (opt) => opt.value === selectedParent
      );
      const sessionName = selectedSession
        ? selectedSession.label
        : "Selected Topic";

      if (session?.access_token) {
        try {
          await fetch(zoomeyApiUrl + "/data_store_memory", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              content: addToChatText,
              session_id: selectedParent,
              user_id: session.user.id,
            }),
          });
        } catch (err) {
          console.error("Error storing memory:", err);
        }

        setTimeout(async () => {
          try {
            await fetch(zoomeyApiUrl + "/restore_context", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                session_id: selectedParent,
                user_id: session.user.id,
              }),
            });
          } catch (err) {
            console.error("Error restoring context:", err);
          }

          setIsAdding(false);

          await Swal.fire({
            title: "Success!",
            text: `Message successfully added to "${sessionName}"`,
            icon: "success",
            confirmButtonText: "Great!",
            confirmButtonColor: "#1A73E8",
            background: "var(--background)",
            color: "var(--text)",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            allowOutsideClick: true,
          });

          handleClose();
        }, 2000);
      } else {
        setIsAdding(false);
        await Swal.fire({
          title: "Success!",
          text: `Message successfully added to "${sessionName}"`,
          icon: "success",
          confirmButtonText: "Great!",
          confirmButtonColor: "#1A73E8",
          background: "var(--background)",
          color: "var(--text)",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false,
          allowOutsideClick: true,
        });

        handleClose();
      }
    } catch (error) {
      console.error("Error adding to session:", error);
      setIsAdding(false);

      await Swal.fire({
        title: "Error!",
        text: "Failed to add message, please try again.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#dc3545",
        background: "var(--background)",
        color: "var(--text)",
      });
    }
  };

  // Build options for Select
  const selectOptions = [
    ...(mainSessions.length > 0
      ? [{ value: mainSessions[0].id, label: "Main Topic", isMain: true }]
      : []),
    ...parentSessions.flatMap((parent) => {
      const parentOption = {
        value: parent.id,
        label: parent.name,
        isChild: false,
      };
      const children = childSessions[parent.id] || [];
      const childOptions = children.map((child) => ({
        value: child.id,
        label: `${child.name} (subtopic)`,
        isChild: true,
      }));
      return [parentOption, ...childOptions];
    }),
  ];

  // Find current selected value
  const selectedValue =
    selectOptions.find((opt) => opt.value === selectedParent) || null;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      centered
      className="addtosession-modal"
    >
      <Modal.Header closeButton className="addtosession-modal-header">
        <Modal.Title>Add to Topic</Modal.Title>
      </Modal.Header>
      <Modal.Body className="addtosession-modal-body">
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="addtosession-label">
              Select Topic
            </Form.Label>
            <Select
              className="addtosession-select"
              classNamePrefix="session-select"
              options={selectOptions}
              value={selectedValue}
              onChange={(option) =>
                setSelectedParent(option ? option.value : "")
              }
              placeholder="Search or select topic..."
              isClearable
              formatOptionLabel={(option) => (
                <span
                  className={
                    option.isMain
                      ? "session-option-main"
                      : option.isChild
                      ? "session-option-child"
                      : "session-option-parent"
                  }
                >
                  {option.label}
                </span>
              )}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="addtosession-modal-footer">
        <Button variant="secondary" onClick={handleClose} disabled={isAdding}>
          Close
        </Button>
        <Button
          className="addtosession-add-btn"
          onClick={addToSession}
          disabled={isAdding || !selectedParent}
        >
          {isAdding ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Adding...
            </>
          ) : (
            "Add"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddToSessionModal;
